/**
 * Formats a paste carries in from wherever the text was copied.
 *
 * Quill reads the source markup's own `color` and `background`, either from an
 * inline `style` or from the `ql-color-*` / `ql-bg-*` classes another Quill editor
 * writes, and applies them to the pasted text. That is why a paragraph copied out
 * of a page that renders it blue lands in the composer blue, on text the author
 * never coloured.
 */
const SOURCE_PALETTE_FORMATS = ['color', 'background'];

/**
 * Clipboard matcher that drops the source's text and highlight colours, so pasted
 * text takes the composer's own.
 *
 * Only colour is dropped: bold, links, headings, lists and every other format
 * survive the paste untouched.
 *
 * `isPasting` is what keeps this to an actual paste. Matchers run on everything
 * the editor converts, which includes the HTML it is handed when a saved comment is
 * opened for editing, and a colour the author chose from the toolbar has to survive
 * that.
 */
export function createPastePaletteStripper(isPasting: () => boolean) {
  return (_node: Node, delta: any): any => {
    if (!isPasting()) {
      return delta;
    }

    (delta?.ops ?? []).forEach((op: any) => {
      if (!op.attributes) {
        return;
      }

      SOURCE_PALETTE_FORMATS.forEach((format) => {
        delete op.attributes[format];
      });

      if (Object.keys(op.attributes).length === 0) {
        // Quill treats an empty attributes object as a format of its own, so it is
        // removed rather than left behind as `{}`.
        delete op.attributes;
      }
    });

    return delta;
  };
}

/**
 * How long after a paste event the stripper stays armed.
 *
 * Quill converts the clipboard from a `setTimeout(…, 1)` inside its own paste
 * handler, so the conversion happens after the event this window opens on. A
 * window rather than a flag because nothing tells us the conversion has finished,
 * and one that closes on its own cannot leak into a later edit.
 */
export const PASTE_WINDOW_MS = 300;
